import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { projectInDtoToEntity } from 'shared/domain/project/mapping/toEntity';
import { projectEntityToModel } from 'shared/domain/project/mapping/toModel';
import { ProjectInDto } from 'shared/domain/project/types/dto';
import { ProjectModel } from 'shared/domain/project/types/model';
import { chooseErrorMessage } from 'shared/utils/errorHandling';
import * as dbProjects from '../../db/projects/projects';
import { broadcastUploadedProjectError } from './error';
import { projectContract } from 'shared/domain/project/projectContract';
import { makeEventPublisher } from 'serviceWorker/broadcasts/publishers';
import { storeAndReadServerAnswer } from 'serviceWorker/broadcasts/storeServerAnswer';

/**
 * Stores the project the server returned and answers its change with the
 * model. IndexedDB failures never reject it (see `storeAndReadServerAnswer`):
 * the answer is then the server's project. Resolves `true` when stored.
 */
function makeStoredProjectBroadcast(
  type: DomainMessagesTypes.createdEntity | DomainMessagesTypes.updatedEntity
) {
  const broadcastModel = makeEventPublisher(projectContract, type);

  return async (projectInDto: ProjectInDto, uniqueId: string): Promise<boolean> => {
    try {
      const { model, stored } = await storeAndReadServerAnswer('project', {
        // Keeps the stored `code`, which the server's answer lacks.
        store: () => dbProjects.updateLocalBatch([projectInDto]),
        readStored: async () => {
          const projectEntity = await dbProjects.getOne(projectInDto._id);
          return projectEntity && projectEntityToModel(projectEntity);
        },
        fromServerAnswer: () => projectEntityToModel(projectInDtoToEntity(projectInDto)),
      });
      broadcastModel(model, uniqueId);
      return stored;
    } catch (error) {
      broadcastUploadedProjectError(error, chooseErrorMessage(error), uniqueId);
      return false;
    }
  };
}

export const broadcastCreatedProject = makeStoredProjectBroadcast(DomainMessagesTypes.createdEntity);

export const broadcastUpdatedProject = makeStoredProjectBroadcast(DomainMessagesTypes.updatedEntity);

/** Archive and restore queued by older pages; the runner already marked the project. */
export const broadcastDeletedProject = makeEventPublisher(projectContract, DomainMessagesTypes.deletedEntity);

export const broadcastRestoredProject = makeEventPublisher(projectContract, DomainMessagesTypes.uploadedEntity);
